import { useEffect, useMemo, useState } from "react";
import { DesignApp } from "./DesignApp";
import { rootReducer } from "./reducers/RootReducer";
import { TranslationProvider } from "./context/Translation/TranslationProvider";

import { useSetTitleAction } from '../../actions/useActions/useSetTitleAction/useSetTitleAction';
import { useAppCompanyState, useAppGlobalDispatch, useAppGlobalState, useAppUserState, useConfigState, useVrsTranslationState } from '../../context/AppContext/AppContext';
import { IRootState } from './context/Store/StoreTypes';
import { StoreProvider } from './context/Store/StoreProvider';
import { getDesignToken } from '../../api/Design/DesignActions';
import { getCurrentUser } from '../../libs/authLib';
import { useToaster } from '../../context/ToasterContext/ToasterContext';
import EmptyStateActions from "../../components/EmptyStateActions/EmptyStateActions";
import { BrandingDesignIcon } from "../../components-redesign/Icons/Icons";

export const DesignEditorPage = () => {
  const { userProfileLarge, isAuthenticated, designToken } = useAppUserState();
  const setTitle = useSetTitleAction();
  const { _T } = useVrsTranslationState();
  const [sessionDataInitiated, setSessionDataInitiated] = useState(false);
  const configState = useConfigState();
  const toastr = useToaster();
  const { isPureDesignUser } = useAppGlobalState();
  const dispatch = useAppGlobalDispatch();
  const { selectedCompanyId, selectedSiteIdForCompany } = useAppCompanyState();

  const [designPageCanBeInitialised, setDesignPageCanBeInitialised] = useState(false);
  const [fileName, setFileName] = useState<string>('');


  useEffect(() => {
    const hash = window.location.hash;

    const queryString = hash.startsWith('#?')
      ? hash.substring(2)
      : hash.startsWith('/#?')
        ? hash.substring(3)
        : hash.substring(1);

    const urlParams = new URLSearchParams(queryString);

    const displayedName = urlParams.get('ciffName');
    const ciffId = urlParams.get('ciffId');
    const printerType = urlParams.get('printerType') || '';
    const printerFormat = urlParams.get('printerFormat') || '';

    const sessionData = {
      ciffDescriptorRequestObj: `{"pageName":"edit","editFile":"${ciffId}"}`,
      ciffName: ciffId,
      ciffPrintFormat: 'Default',
      editorTemplateTimeStamp: 'Not Initialised',
      redoStack: '[]',
      undoStack: '[]',
      subImage: '1',
      subImageAfterRefresh: '1',
      webClientPage: 'CiffEditor',
      printerFormat,
      printerType,
      displayedName
    };

    (window as any).MemorySessionStorage = sessionData;

    setSessionDataInitiated(true);
  }, []);

  useEffect(() => {
    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Store original body styles
    const originalPadding = document.body.style.paddingRight;
    const originalOverflow = document.body.style.overflow;

    // Apply changes
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.style.overflow = 'hidden';

    // Cleanup function to revert changes
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPadding;
    };
  }, []);


  const displayedName = useMemo(() => {
    return sessionDataInitiated ? (window as any).MemorySessionStorage["displayedName"] : "";
  }, [sessionDataInitiated, fileName]);

  const ciffName = useMemo(() => {
    return sessionDataInitiated ? (window as any).MemorySessionStorage["ciffName"] : "";
  }, [sessionDataInitiated, fileName]);

  useEffect(() => {
    setTitle(
      _T('Design Editor'),
       <BrandingDesignIcon />,
      {
        parents: [
          {
            id: _T('Design Templates'),
            textOnly: true,
            link: "/new-design",
          },
          {
            id: `${displayedName}`,
            textOnly: true,
          },
        ],
      },
      true,
      true, // titleHidden
      true // smallPadding
    );
  }, [setTitle, _T, ciffName]);


  useEffect(() => {
    if (isAuthenticated && !designToken) {
      (async () => {
        try {
          const currentUser = await getCurrentUser();
          // For pure design users, siteId, companyId and userId are the same guid we generated when the user registers
          const isExternal = configState.appDataInitialization.firstSiteData.isExternal;
          await getDesignToken(dispatch, userProfileLarge, isPureDesignUser ? currentUser?.userId : selectedSiteIdForCompany, isExternal, false);
          setTimeout(() => {
            // initisally set to false to prevent flicker of the design page and calling template with empty token
            setDesignPageCanBeInitialised(true);
          }, 1000);

        } catch (err) {
          if (err instanceof Error) {
            toastr.error(err.message);
          }
        }
      })();
    } else if (isAuthenticated && designToken) {
      setDesignPageCanBeInitialised(true);
    }
  }, [isAuthenticated, designToken, userProfileLarge, selectedSiteIdForCompany, isPureDesignUser, dispatch, configState.appDataInitialization.firstSiteData.isExternal, toastr]);


  return designPageCanBeInitialised ? <StoreProvider reducer={rootReducer} initialState={rootReducer({} as IRootState, "INITALISE_STATE")}>
    {isAuthenticated && <TranslationProvider>
      {(selectedCompanyId !== "0" || isPureDesignUser) && sessionDataInitiated && displayedName && ciffName && <DesignApp setCiffFileName={setFileName} />}
      {selectedCompanyId === "0" ? <EmptyStateActions
        activeCompanyId={selectedCompanyId}
        selectSiteButtonText={_T("Select a Site")}
      /> : null
      }
    </TranslationProvider>}
  </StoreProvider> : null;
};

